import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import { Mark, mergeAttributes, Extension, Node as TTNode } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import ImageResize from 'tiptap-extension-resize-image';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import TextStyle from '@tiptap/extension-text-style';
import { toast } from 'sonner';
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough, ListBullets, ListNumbers,
  ListChecks, Quotes, Code, TextHOne, TextHTwo, TextHThree, LinkSimple, ImageSquare,
  Table as TableIcon, Minus, ChatText, TextAa, CaretDown, RowsPlusTop, RowsPlusBottom,
  ColumnsPlusLeft, ColumnsPlusRight, XCircle, TextAlignLeft, TextAlignCenter,
  TextAlignRight, PaintBucket, ArrowsInLineHorizontal, ArrowsOutLineHorizontal, Rows,
  Columns, Trash, ArrowLineDown,
} from '@phosphor-icons/react';
import { knowledgeApi } from './knowledgeApi';
import LinkPickerDialog from './LinkPickerDialog';

// ─── FontSize extension (Batch 4 · iter_154) ─────────────────────────────
// Extends TextStyle to carry a fontSize attribute that renders as an inline
// `style="font-size: Xpx"` on a <span>. Applies to selection only so
// H1/H2/H3 are never overridden. Existing articles without any fontSize
// mark render at the article's default (16px / 1rem) — no migration needed.
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() { return { types: ['textStyle'] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el) => el.style.fontSize?.replace(/['"]+/g, '') || null,
          renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

// Named tiers exposed to the user. Values are in px so they can nest
// safely inside blockquotes/tables without cascading against tailwind.
const FONT_SIZES = [
  { id: 'default', label: 'デフォルト', px: null },
  { id: 'small',   label: '小',        px: '13px' },
  { id: 'normal',  label: '標準',      px: '16px' },
  { id: 'large',   label: '大',        px: '20px' },
  { id: 'xlarge',  label: '特大',      px: '24px' },
];

// Batch 5 · iter_155 — extend TableCell + TableHeader with `align` and
// `backgroundColor` attributes so the contextual table toolbar can toggle
// them via `setCellAttribute`. Rendered as inline CSS on the <td> / <th>.
// Legacy tables have no `align` / `backgroundColor` attribute → default
// null → nothing rendered → existing 3×3 tables display unchanged.
const CELL_ATTRS = {
  align: {
    default: null,
    parseHTML: (el) => el.style.textAlign || null,
    renderHTML: (attrs) => (attrs.align ? { style: `text-align: ${attrs.align}` } : {}),
  },
  backgroundColor: {
    default: null,
    parseHTML: (el) => el.style.backgroundColor || null,
    renderHTML: (attrs) => (attrs.backgroundColor ? { style: `background-color: ${attrs.backgroundColor}` } : {}),
  },
};
const TableCellExt = TableCell.extend({
  addAttributes() { return { ...this.parent?.(), ...CELL_ATTRS }; },
});
const TableHeaderExt = TableHeader.extend({
  addAttributes() { return { ...this.parent?.(), ...CELL_ATTRS }; },
});

// Batch 5 — cell background palette shown in the contextual table toolbar.
// Uses the same design-system tones already present elsewhere in the app.
const CELL_BG_SWATCHES = [
  { id: 'clear',  color: null,      label: 'なし' },
  { id: 'yellow', color: '#fef3c7', label: '黄' },
  { id: 'green',  color: '#dcfce7', label: '緑' },
  { id: 'blue',   color: '#dbeafe', label: '青' },
  { id: 'pink',   color: '#fce7f3', label: '桃' },
  { id: 'gray',   color: '#f4f4f5', label: '灰' },
  { id: 'red',    color: '#fee2e2', label: '赤' },
  { id: 'purple', color: '#ede9fe', label: '紫' },
];

// Batch 7 · iter_157 — image upload client-side limits (kept in sync with
// the backend `/api/knowledge/media` validation).
const IMG_ALLOWED_MIMES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const IMG_MAX_BYTES = 10 * 1024 * 1024;
const IMG_MAX_DIM = 8000;
// Size presets shown in the image bubble menu. Value is the CSS width
// applied via `containerStyle` — the extension already parses % widths.
const IMG_SIZE_PRESETS = [
  { id: '25',   label: '25%',  width: '25%' },
  { id: '50',   label: '50%',  width: '50%' },
  { id: '75',   label: '75%',  width: '75%' },
  { id: '100',  label: '100%', width: '100%' },
];
const IMG_ALIGN_PRESETS = [
  // Batch 7 · iter_157 — alignment is expressed as `margin` on the
  // inner container (the one with `width: N%`). This survives the
  // extension's CSS sanitizer, which whitelists `margin` but NOT
  // `justify-content`. Because the wrapper is `display: flex`, a
  // flex child with `margin-left: auto` / `margin-right: auto` gets
  // pushed to the corresponding edge. Shorthand values are
  // top / right / bottom / left.
  { id: 'left',   label: '左寄せ', margin: '0 auto 0 0' },
  { id: 'center', label: '中央',   margin: '0 auto' },
  { id: 'right',  label: '右寄せ', margin: '0 0 0 auto' },
];

// Batch 6 · iter_156 — extend Link with a `data-kb-article-id` attribute.
// Internal (knowledge-article) links carry this attribute + an href pointing
// at `/knowledge/p/{article_id}`. The article router already accepts either
// the UUID or the slug and normalises the URL to the current slug at load,
// so links stay valid across renames (Q4). External links have no data
// attribute and can point anywhere http/https/mailto. Unsafe protocols
// (javascript:, data:, vbscript:) are rejected inside LinkPickerDialog
// before the mark is applied — TipTap's core Link also rejects them.
const LinkExt = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-kb-article-id': {
        default: null,
        parseHTML: (el) => el.getAttribute('data-kb-article-id') || null,
        renderHTML: (attrs) => (attrs['data-kb-article-id'] ? { 'data-kb-article-id': attrs['data-kb-article-id'] } : {}),
      },
    };
  },
});

// Hoisted presentational buttons — kept stable across renders (see
// react/no-unstable-nested-components). Pure props → tiny CSS-only wrappers.
function ToolbarBtn({ active, onClick, title, children, testid, disabled }) {
  return (
    <button type="button" title={title} data-testid={testid} onClick={onClick} disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors ${
        disabled ? 'text-zinc-300 cursor-not-allowed'
          : active ? 'bg-blue-100 text-blue-700'
          : 'text-zinc-500 hover:bg-zinc-100'
      }`}>
      {children}
    </button>
  );
}

function BubbleBtn({ active, onClick, title, children, testid, disabled }) {
  return (
    <button type="button" title={title} data-testid={testid} disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`h-7 w-7 inline-flex items-center justify-center rounded transition-colors ${
        disabled ? 'text-zinc-300 cursor-not-allowed'
          : active ? 'bg-blue-100 text-blue-700'
          : 'text-zinc-600 hover:bg-zinc-100'
      }`}>
      {children}
    </button>
  );
}

// Batch 5 — Row/col size picker. Hover-grid up to 10×10 plus a numeric
// input for larger sizes. Renders as a positioned popover; caller decides
// where to place it. onPick(rows, cols) commits the choice; onClose is
// called for cancel (click-away / Esc).
function TableSizePicker({ onPick, onClose }) {
  const [hover, setHover] = useState({ r: 3, c: 3 });
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const rootRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) onClose?.(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const grid = [];
  for (let r = 1; r <= 10; r += 1) {
    for (let c = 1; c <= 10; c += 1) grid.push({ r, c });
  }
  return (
    <div ref={rootRef} data-testid="kb-table-size-picker"
      className="rounded-lg border border-zinc-200 bg-white p-3 shadow-xl">
      <div className="mb-2 text-xs font-semibold text-zinc-700">
        {hover.r} × {hover.c}
      </div>
      <div className="grid grid-cols-10 gap-0.5" onMouseLeave={() => setHover({ r: rows, c: cols })}>
        {grid.map(({ r, c }) => {
          const active = r <= hover.r && c <= hover.c;
          return (
            <button key={`${r}-${c}`} type="button"
              onMouseEnter={() => setHover({ r, c })}
              onClick={() => onPick(r, c)}
              data-testid={`kb-table-cell-${r}-${c}`}
              aria-label={`${r} 行 × ${c} 列`}
              className={`h-4 w-4 rounded-sm border transition-colors ${
                active ? 'bg-blue-500 border-blue-600' : 'bg-zinc-50 border-zinc-200 hover:border-blue-400'
              }`} />
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-xs text-zinc-500">
        <label className="inline-flex items-center gap-1">
          行
          <input type="number" min="1" max="50" value={rows}
            onChange={(e) => setRows(clamp(parseInt(e.target.value, 10) || 1, 1, 50))}
            data-testid="kb-table-rows-input"
            className="h-7 w-14 rounded border border-zinc-200 px-1.5 text-center" />
        </label>
        <label className="inline-flex items-center gap-1">
          列
          <input type="number" min="1" max="20" value={cols}
            onChange={(e) => setCols(clamp(parseInt(e.target.value, 10) || 1, 1, 20))}
            data-testid="kb-table-cols-input"
            className="h-7 w-14 rounded border border-zinc-200 px-1.5 text-center" />
        </label>
        <button type="button"
          onClick={() => onPick(rows, cols)}
          data-testid="kb-table-insert-btn"
          className="ml-auto h-7 rounded bg-blue-600 px-2.5 text-white text-xs font-medium hover:bg-blue-700">
          挿入
        </button>
      </div>
    </div>
  );
}

// Font-size picker — kept outside RichEditor because the ESLint rule
// react/no-unstable-nested-components would otherwise trash DOM/state on
// every parent re-render. Props supply everything picker-specific.
function FontSizePicker({ testidPrefix, currentTier, isOpen, setOpen, onPick, menuRef }) {
  return (
    <div className="relative" ref={menuRef}>
      <button type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testidPrefix}-fontsize`}
        title={`文字サイズ (現在: ${currentTier.label})`}
        className="h-8 inline-flex items-center gap-1 px-1.5 rounded-md text-zinc-500 hover:bg-zinc-100">
        <TextAa size={17} />
        <span className="text-[11px] font-medium min-w-[24px] text-left">{currentTier.label}</span>
        <CaretDown size={10} weight="bold" />
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-32 rounded-lg border border-zinc-200 bg-white py-1 shadow-xl" data-testid={`${testidPrefix}-fontsize-menu`}>
          {FONT_SIZES.map((s) => (
            <button key={s.id} type="button"
              onMouseDown={(e) => { e.preventDefault(); onPick(s); }}
              data-testid={`${testidPrefix}-fontsize-${s.id}`}
              className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${
                currentTier.id === s.id ? 'bg-blue-50 text-blue-700' : 'text-zinc-700 hover:bg-zinc-50'
              }`}
              style={s.px ? { fontSize: s.px } : undefined}>
              <span>{s.label}</span>
              {currentTier.id === s.id && <span className="text-[10px] text-blue-600">●</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// 改ページ — manual page break block (<div class="page-break">). Atom node so
// it can never swallow content; print CSS (knowledgeDocument.css) forces a
// new page after it, screen CSS shows a subtle dashed indicator.
const PageBreak = TTNode.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  selectable: true,
  parseHTML() { return [{ tag: 'div.page-break' }]; },
  renderHTML() { return ['div', { class: 'page-break', 'data-testid': 'kb-page-break' }]; },
  addCommands() {
    return {
      setPageBreak: () => ({ chain }) => chain().insertContent({ type: 'pageBreak' }).createParagraphNear().run(),
    };
  },
});

// 旧「プロジェクト → ナレッジ同期」（Jun 2026 廃止）で生成された既存記事の data-sb-*
// 属性を TipTap の再シリアライズで失わないための後方互換ハンドラ。
// 新規記事では属性が null のまま何もレンダリングされない（無害・生成なし）。
const SB_MARKER_ATTRS = ['data-sb-proj', 'data-sb-task', 'data-sb-field'];
const GeneratedMarkers = Extension.create({
  name: 'kbGeneratedMarkers',
  addGlobalAttributes() {
    return [{
      types: ['heading', 'paragraph'],
      attributes: Object.fromEntries(SB_MARKER_ATTRS.map((a) => [a, {
        default: null,
        parseHTML: (el) => el.getAttribute(a) || null,
        renderHTML: (attrs) => (attrs[a] ? { [a]: attrs[a] } : {}),
      }])),
    }];
  },
});

// Odoo-style collapsible heading sections. Session-only fold state kept in
// plugin state (positions mapped through transactions) — decorations hide
// the section visually; the document content is NEVER modified, so
// save/reload and collab are unaffected.
const foldPluginKey = new PluginKey('kbHeadingFold');

function makeFoldToggle(view, pos, isCollapsed) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.contentEditable = 'false';
  btn.className = `kb-fold-toggle${isCollapsed ? ' kb-folded' : ''}`;
  btn.setAttribute('data-testid', 'kb-heading-fold-toggle');
  btn.setAttribute('aria-expanded', String(!isCollapsed));
  const label = isCollapsed ? 'セクションを展開' : 'セクションを折りたたむ';
  btn.title = label;
  btn.setAttribute('aria-label', label);
  btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M5 3l6 5-6 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  btn.addEventListener('mousedown', (e) => { e.preventDefault(); e.stopPropagation(); });
  btn.addEventListener('click', (e) => {
    e.preventDefault(); e.stopPropagation();
    view.dispatch(view.state.tr.setMeta(foldPluginKey, { toggle: pos }));
  });
  return btn;
}

const CollapsibleHeadings = Extension.create({
  name: 'collapsibleHeadings',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: foldPluginKey,
        state: {
          init: () => [],
          apply(tr, value) {
            let next = value;
            if (tr.docChanged) {
              next = [];
              for (const pos of value) {
                const r = tr.mapping.mapResult(pos);
                if (!r.deleted) next.push(r.pos);
              }
            }
            const meta = tr.getMeta(foldPluginKey);
            if (meta && typeof meta.toggle === 'number') {
              next = next.includes(meta.toggle)
                ? next.filter((p) => p !== meta.toggle)
                : [...next, meta.toggle];
            }
            return next;
          },
        },
        props: {
          decorations(state) {
            const collapsedSet = new Set(foldPluginKey.getState(state) || []);
            const decos = [];
            const children = [];
            state.doc.forEach((node, offset) => children.push({ node, pos: offset }));
            for (let i = 0; i < children.length; i += 1) {
              const { node, pos } = children[i];
              if (node.type.name !== 'heading') continue;
              const level = node.attrs.level;
              let end = i + 1;
              while (end < children.length) {
                const n = children[end].node;
                if (n.type.name === 'heading' && n.attrs.level <= level) break;
                end += 1;
              }
              if (end === i + 1) continue; // no content beneath — no toggle
              const isCollapsed = collapsedSet.has(pos);
              decos.push(Decoration.node(pos, pos + node.nodeSize, { class: `kb-foldable${isCollapsed ? ' kb-folded' : ''}` }));
              decos.push(Decoration.widget(pos + 1, (view) => makeFoldToggle(view, pos, isCollapsed), { side: -1, key: `kb-fold-${pos}-${isCollapsed ? 1 : 0}` }));
              if (isCollapsed) {
                for (let j = i + 1; j < end; j += 1) {
                  const c = children[j];
                  decos.push(Decoration.node(c.pos, c.pos + c.node.nodeSize, { class: 'kb-fold-hidden' }));
                }
              }
            }
            return DecorationSet.create(state.doc, decos);
          },
        },
      }),
    ];
  },
});

const CommentMark = Mark.create({
  name: 'comment',
  inclusive: false,
  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-comment-id'),
        renderHTML: (attrs) => (attrs.commentId ? { 'data-comment-id': attrs.commentId } : {}),
      },
    };
  },
  parseHTML() { return [{ tag: 'span[data-comment-id]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'kb-comment-mark' }), 0];
  },
  addCommands() {
    return {
      setCommentMark: (commentId) => ({ commands }) => commands.setMark('comment', { commentId }),
      unsetCommentMark: (commentId) => ({ state, dispatch }) => {
        const markType = state.schema.marks.comment;
        if (!markType) return false;
        const { tr, doc } = state;
        let changed = false;
        doc.descendants((node, pos) => {
          if (!node.isText) return;
          node.marks.forEach((m) => {
            if (m.type === markType && m.attrs.commentId === commentId) {
              tr.removeMark(pos, pos + node.nodeSize, markType);
              changed = true;
            }
          });
        });
        if (changed && dispatch) dispatch(tr);
        return changed;
      },
    };
  },
});

// Batch 7 · iter_157 — probe pixel dimensions of a file client-side by
// reading it into an `<img>`. Rejects (via a promise reject) for bytes
// that don't decode as an image at all, so drop-drop/paste flows can
// bail out before hitting the network. Objects URLs are revoked in a
// finally block regardless of outcome.
function probeImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => { const w = img.naturalWidth; const h = img.naturalHeight; URL.revokeObjectURL(url); resolve({ w, h }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('画像として読み込めませんでした')); };
    img.src = url;
  });
}

// Batch 7 · iter_157 — one-stop validator + uploader. Returns the
// server-generated `{url, width, height}` payload on success, or throws
// with a user-facing (JA) message on any pre-flight or upload failure.
// Callers should try/catch and toast the error themselves.
async function validateAndUploadImage(file) {
  if (!IMG_ALLOWED_MIMES.has(file.type)) {
    throw new Error('対応していない画像形式です (PNG / JPEG / WebP / GIF のみ)');
  }
  if (file.size > IMG_MAX_BYTES) {
    throw new Error('画像サイズは10MB以下にしてください');
  }
  let dims = null;
  try { dims = await probeImageDimensions(file); } catch (e) { throw new Error(e.message || '画像として読み込めませんでした'); }
  if (dims.w > IMG_MAX_DIM || dims.h > IMG_MAX_DIM) {
    throw new Error(`画像の解像度が大きすぎます (${IMG_MAX_DIM}×${IMG_MAX_DIM} まで)`);
  }
  const res = await knowledgeApi.uploadMedia(file);
  return res;
}

const SLASH_ITEMS_BASE = [
  { id: 'h1', label: '見出し1', kw: ['h1', 'heading', 'midashi', '見出し'], Icon: TextHOne, run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 1 }).run() },
  { id: 'h2', label: '見出し2', kw: ['h2', 'heading', '見出し'], Icon: TextHTwo, run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 2 }).run() },
  { id: 'h3', label: '見出し3', kw: ['h3', 'heading', '見出し'], Icon: TextHThree, run: (e, r) => e.chain().focus().deleteRange(r).toggleHeading({ level: 3 }).run() },
  { id: 'bullet', label: '箇条書き', kw: ['ul', 'bullet', 'list', 'リスト', 'かじょう'], Icon: ListBullets, run: (e, r) => e.chain().focus().deleteRange(r).toggleBulletList().run() },
  { id: 'ordered', label: '番号付きリスト', kw: ['ol', 'number', 'リスト', 'ばんごう'], Icon: ListNumbers, run: (e, r) => e.chain().focus().deleteRange(r).toggleOrderedList().run() },
  { id: 'task', label: 'ToDoリスト', kw: ['todo', 'task', 'check', 'チェック'], Icon: ListChecks, run: (e, r) => e.chain().focus().deleteRange(r).toggleTaskList().run() },
  { id: 'quote', label: '引用', kw: ['quote', 'blockquote', 'いんよう'], Icon: Quotes, run: (e, r) => e.chain().focus().deleteRange(r).toggleBlockquote().run() },
  { id: 'code', label: 'コードブロック', kw: ['code', 'コード'], Icon: Code, run: (e, r) => e.chain().focus().deleteRange(r).toggleCodeBlock().run() },
  { id: 'divider', label: '区切り線', kw: ['hr', 'divider', 'くぎり', 'せん'], Icon: Minus, run: (e, r) => e.chain().focus().deleteRange(r).setHorizontalRule().run() },
  { id: 'pagebreak', label: '改ページ', kw: ['page', 'break', 'pagebreak', 'かいぺーじ', 'ぺーじ'], Icon: ArrowLineDown, run: (e, r) => e.chain().focus().deleteRange(r).setPageBreak().run() },
  // 'table' is defined inside the component so its `run` can close over
  // openTablePicker() — Batch 5 opens a size-picker instead of inserting
  // a hardcoded 3×3.
];

export default function RichEditor({
  value, onChange, editable = true, placeholder,
  ydoc, provider, user, initialHtml, onCreateComment, onSelectComment, onEditorReady,
  tree = [],
}) {
  const collab = !!ydoc;
  const fileRef = useRef(null);
  const editorRef = useRef(null);
  const slashRef = useRef(null);
  const seededRef = useRef(false);
  const [slash, setSlash] = useState(null);
  const [sizeMenuOpen, setSizeMenuOpen] = useState(false);
  const sizeMenuRef = useRef(null);
  const [tablePicker, setTablePicker] = useState(null);
  // Batch 6 — link picker dialog state. Holds the "initial" mark (if any)
  // so the dialog can open in edit mode.
  const [linkDialog, setLinkDialog] = useState(null); // { open: true, initial: {href, articleId, text} } | null
  useEffect(() => { slashRef.current = slash; }, [slash]);

  const openTablePicker = (range = null) => {
    const ed = editorRef.current;
    if (!ed) return;
    const { from } = ed.state.selection;
    let anchor;
    try { anchor = ed.view.coordsAtPos(from); } catch { anchor = null; }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = 244; // approx picker width
    const height = 232; // approx picker height
    let top = (anchor?.bottom ?? 120) + 8;
    let left = (anchor?.left ?? 40);
    // clamp inside viewport with 12px margin
    if (left + width > vw - 12) left = Math.max(12, vw - 12 - width);
    if (top + height > vh - 12) top = Math.max(12, (anchor?.top ?? 120) - height - 8);
    setTablePicker({ top, left, range });
  };
  const commitTable = (rows, cols) => {
    const ed = editorRef.current;
    if (!ed) { setTablePicker(null); return; }
    const range = tablePicker?.range;
    const chain = ed.chain().focus();
    if (range) chain.deleteRange(range);
    chain.insertTable({ rows, cols, withHeaderRow: true }).run();
    setTablePicker(null);
  };

  // Slash items with the table entry wired into the picker.
  const SLASH_ITEMS = [
    ...SLASH_ITEMS_BASE,
    {
      id: 'table', label: '表', kw: ['table', 'ひょう', 'テーブル'], Icon: TableIcon,
      run: (e, r) => { e.chain().focus().deleteRange(r).run(); openTablePicker(null); },
    },
  ];

  // Click-away for the font-size dropdown (rendered inside the pinned toolbar
  // AND inside the bubble menu — both share this state so only one is ever
  // open at a time).
  useEffect(() => {
    if (!sizeMenuOpen) return undefined;
    const onDoc = (e) => { if (sizeMenuRef.current && !sizeMenuRef.current.contains(e.target)) setSizeMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [sizeMenuOpen]);

  const detectSlash = (editor) => {
    const { state } = editor;
    const { from, empty } = state.selection;
    if (!empty || !editor.isEditable) { setSlash(null); return; }
    const before = state.doc.textBetween(Math.max(0, from - 40), from, '\n', '\0');
    const m = /(?:^|\s)\/([^\s/]*)$/.exec(before);
    if (!m) { setSlash(null); return; }
    const query = m[1].toLowerCase();
    const items = SLASH_ITEMS.filter((it) => !query || it.label.toLowerCase().includes(query) || it.kw.some((k) => k.includes(query)));
    if (!items.length) { setSlash(null); return; }
    const range = { from: from - m[1].length - 1, to: from };
    const coords = editor.view.coordsAtPos(from);
    setSlash({ items, range, top: coords.bottom + 4, left: coords.left, index: 0 });
  };

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, ...(collab ? { history: false } : {}) }),
      Underline,
      LinkExt.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      // Batch 7 · iter_157 — swap plain Image for ImageResize so users
      // can grab the corner handles + inline width attribute is saved
      // to article HTML. Renders as `imageResize` node (`<img>` output
      // is identical from a reader's perspective — the resize handles
      // only appear when `editable=true`). Legacy articles that stored
      // `<img>` from the old Image node still parse because ImageResize
      // registers `tag: 'img[src]'` in its parse rules.
      //
      // IMPORTANT (iter_157 follow-up): do NOT pass `maxWidth` /
      // `minWidth` here. The extension uses them to force pixel-based
      // sizing via `applyResizeLimits`, which:
      //   (a) rescales tiny insertions (1×1 PNG) up to `maxWidth`,
      //   (b) overwrites our `width: 50%` preset with a fixed px value.
      // With no clamps set, `applyResizeLimits` early-returns whenever
      // the container style is percent-based, so our size presets
      // survive both the initial insert AND the reload round-trip
      // (containerstyle is emitted as an HTML attr and parsed back).
      ImageResize.configure({ HTMLAttributes: { class: 'kb-img' } }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({ resizable: true }),
      TableRow, TableHeaderExt, TableCellExt,
      CommentMark,
      TextStyle,
      FontSize,
      PageBreak,
      GeneratedMarkers,
      ...(editable ? [CollapsibleHeadings] : []),
      Placeholder.configure({ placeholder: placeholder || '本文を入力…　"/" でブロックを挿入' }),
      ...(collab ? [
        Collaboration.configure({ document: ydoc }),
        CollaborationCursor.configure({ provider, user: { name: user?.name || '匿名', color: user?.color || '#3b82f6' } }),
      ] : []),
    ],
    content: collab ? undefined : (value || ''),
    editorProps: {
      attributes: { class: 'tiptap focus:outline-none', 'data-testid': 'knowledge-editor-content' },
      // Batch 6 · iter_156 — drop handler for the sidebar drag-drop flow.
      // When an article node is dragged from PageTree (which sets a
      // `application/x-sensbey-article` dataTransfer payload), we
      // (a) block the default drop behaviour (would otherwise insert
      // plaintext of the dragged tile), (b) insert a link at the
      // drop position with the stable article `id` as href + a
      // `data-kb-article-id` marker.
      // Batch 7 · iter_157 — fall through to image handling when the
      // drop carries File(s) — upload each image + insert an
      // `imageResize` node at the drop coordinates. This is what makes
      // Windows Explorer / Finder drag-drop feel native.
      handleDrop: (view, event) => {
        const raw = event?.dataTransfer?.getData?.('application/x-sensbey-article');
        if (raw) {
          try {
            const payload = JSON.parse(raw);
            if (!payload?.id) return false;
            const droppedInto = view.posAtCoords({ left: event.clientX, top: event.clientY });
            if (!droppedInto) return false;
            const title = (payload.title || '無題のページ').slice(0, 80);
            const linkMark = view.state.schema.marks.link.create({
              href: `/knowledge/p/${payload.id}`,
              'data-kb-article-id': payload.id,
              target: '_blank',
              rel: 'noopener noreferrer',
            });
            const textNode = view.state.schema.text(title, [linkMark]);
            const tr = view.state.tr.insert(droppedInto.pos, textNode);
            view.dispatch(tr);
            event.preventDefault();
            return true;
          } catch { /* fall through */ }
        }
        // Image file drop (Windows Explorer, Finder, other tabs, etc.)
        const files = Array.from(event?.dataTransfer?.files || []).filter((f) => f.type?.startsWith('image/'));
        if (!files.length) return false;
        event.preventDefault();
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        const insertPos = coords?.pos ?? view.state.selection.from;
        (async () => {
          for (const file of files) {
            const t = toast.loading(`画像をアップロード中: ${file.name || 'image'}`);
            try {
              const { url } = await validateAndUploadImage(file);
              const node = view.state.schema.nodes.imageResize.create({ src: url });
              view.dispatch(view.state.tr.insert(insertPos, node));
              toast.success('画像を挿入しました', { id: t });
            } catch (e) {
              toast.error(e?.response?.data?.detail || e?.message || 'アップロードに失敗しました', { id: t });
            }
          }
        })();
        return true;
      },
      // Batch 7 · iter_157 — clipboard-paste handler. When the user
      // copies an image from a browser / Snipping Tool / macOS
      // screenshot and pastes into the editor, browsers surface it as
      // a `File` on `clipboardData.items`. Upload each and insert at
      // the current caret position. Non-image pastes (plain text,
      // pasted HTML) fall through to ProseMirror's default handler.
      handlePaste: (view, event) => {
        const items = event?.clipboardData?.items || [];
        const files = [];
        for (const it of items) {
          if (it.kind === 'file') {
            const f = it.getAsFile();
            if (f && f.type?.startsWith('image/')) files.push(f);
          }
        }
        if (!files.length) return false;
        event.preventDefault();
        const insertPos = view.state.selection.from;
        (async () => {
          for (const file of files) {
            const t = toast.loading('クリップボードの画像をアップロード中');
            try {
              const { url } = await validateAndUploadImage(file);
              const node = view.state.schema.nodes.imageResize.create({ src: url });
              view.dispatch(view.state.tr.insert(insertPos, node));
              toast.success('画像を挿入しました', { id: t });
            } catch (e) {
              toast.error(e?.response?.data?.detail || e?.message || 'アップロードに失敗しました', { id: t });
            }
          }
        })();
        return true;
      },
      handleClick: (view, pos) => {
        try {
          const marks = view.state.doc.resolve(pos).marks();
          const cm = marks.find((m) => m.type.name === 'comment');
          if (cm && onSelectComment) onSelectComment(cm.attrs.commentId);
        } catch { /* ignore */ }
        return false;
      },
      handleKeyDown: (view, event) => {
        const s = slashRef.current;
        if (!s) return false;
        if (event.key === 'ArrowDown') { setSlash((x) => x && ({ ...x, index: (x.index + 1) % x.items.length })); return true; }
        if (event.key === 'ArrowUp') { setSlash((x) => x && ({ ...x, index: (x.index - 1 + x.items.length) % x.items.length })); return true; }
        if (event.key === 'Enter') { const it = s.items[s.index]; runItem(it); return true; }
        if (event.key === 'Escape') { setSlash(null); return true; }
        return false;
      },
    },
    // Batch 7 · iter_159 — while collab is enabled AND we're still
    // waiting for a legitimate `initialHtml` to arrive, suppress
    // `onChange` so ProseMirror's mount-time empty transaction can't
    // bubble up and clobber the article's stored content via
    // ArticleEditor's auto-save chain. For BRAND-NEW articles created
    // without content_html, `initialHtml` is '' from the start — we
    // must NOT block onChange in that case, or the user's typed text
    // would never propagate to save().
    onUpdate: ({ editor }) => {
      if (collab && !seededRef.current && initialHtml) { detectSlash(editor); return; }
      onChange?.(editor.getHTML());
      detectSlash(editor);
    },
    onSelectionUpdate: ({ editor }) => detectSlash(editor),
    onBlur: () => setTimeout(() => setSlash(null), 150),
  }, []);

  useEffect(() => { editorRef.current = editor; if (editor) onEditorReady?.(editor); }, [editor, onEditorReady]);
  useEffect(() => () => editor?.destroy(), [editor]);

  // 共同編集: 初回同期後、ドキュメントが空なら既存HTMLで初期化(1回のみ)
  // Batch 7 · iter_158 — the 2.5 s fallback seed handles environments
  // where the collab WebSocket never fires `sync` (preview, offline,
  // proxied). CRITICAL: only flip `seededRef.current` to `true` after
  // we actually seeded content. If the timer fires while `initialHtml`
  // is still `''` (async article fetch hasn't resolved yet), we'd
  // otherwise permanently disarm the fallback — leaving the editor
  // stuck empty when initialHtml later populates.
  useEffect(() => {
    if (!collab || !editor || !provider || seededRef.current) return undefined;
    const seed = () => {
      if (seededRef.current) return;
      if (!initialHtml) return;             // wait for the parent to fetch it
      if (editor.isEmpty) editor.commands.setContent(initialHtml, false);
      seededRef.current = true;
    };
    if (provider.synced) { seed(); return undefined; }
    const h = () => seed();
    provider.on('sync', h);
    const fallback = setTimeout(seed, 2500);
    return () => { provider.off('sync', h); clearTimeout(fallback); };
  }, [collab, editor, provider, initialHtml]);

  const runItem = (it) => {
    const ed = editorRef.current; const s = slashRef.current;
    if (!ed || !s || !it) return;
    it.run(ed, s.range); setSlash(null);
  };

  const onImagePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editorRef.current) return;
    const t = toast.loading('画像をアップロード中');
    try {
      const { url } = await validateAndUploadImage(file);
      editorRef.current.chain().focus().setImage({ src: url }).run();
      toast.success('画像を挿入しました', { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'アップロードに失敗しました', { id: t });
    }
  };

  const addComment = async () => {
    const ed = editorRef.current;
    if (!ed || !onCreateComment) return;
    const { from, to, empty } = ed.state.selection;
    if (empty) return;
    const quote = ed.state.doc.textBetween(from, to, ' ');
    const commentId = (window.crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const ok = await onCreateComment(commentId, quote);
    if (ok) ed.chain().focus().setTextSelection({ from, to }).setCommentMark(commentId).run();
  };

  if (!editor) return <div className="py-8 text-sm text-zinc-400">エディタを準備中…</div>;

  const openLinkDialog = () => {
    const attrs = editor.getAttributes('link') || {};
    const selText = (() => {
      const { from, to, empty } = editor.state.selection;
      if (empty) return '';
      try { return editor.state.doc.textBetween(from, to, ' '); } catch { return ''; }
    })();
    setLinkDialog({
      open: true,
      initial: attrs.href
        ? { href: attrs.href, articleId: attrs['data-kb-article-id'] || null, text: selText }
        : { text: selText },
    });
  };

  const applyLinkFromDialog = (payload) => {
    // payload: { href, articleId, text, target }
    const attrs = { href: payload.href, target: payload.target || '_blank', rel: 'noopener noreferrer' };
    if (payload.articleId) attrs['data-kb-article-id'] = payload.articleId;
    const { empty } = editor.state.selection;
    // Empty selection → insert visible link text with the mark. For an
    // article pick this is the article title; for an external URL pick
    // this is the URL itself (unless the caller explicitly supplied a
    // display text via payload.text).
    if (empty) {
      const view = editor.view;
      const pos = view.state.selection.from;
      const rawText = payload.text || (payload.articleId ? 'リンク' : payload.href);
      const title = rawText.slice(0, 200);
      const linkMark = view.state.schema.marks.link.create(attrs);
      const textNode = view.state.schema.text(title, [linkMark]);
      view.dispatch(view.state.tr.insert(pos, textNode).scrollIntoView());
      return;
    }
    // Non-empty selection → apply / update the link mark over that range.
    editor.chain().focus().extendMarkRange('link').setLink(attrs).run();
  };

  const applyLinkRemove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  };

  const selEmpty = editor.state.selection.empty;

  // Batch 4 — font-size helpers. `currentSize` reflects the exact size on
  // the selection so the dropdown label updates as the caret moves.
  const currentSize = editor.getAttributes('textStyle').fontSize || null;
  const currentSizeTier = FONT_SIZES.find((s) => s.px === currentSize) || FONT_SIZES[0];
  const applyFontSize = (tier) => {
    if (!tier.px) editor.chain().focus().unsetFontSize().run();
    else editor.chain().focus().setFontSize(tier.px).run();
    setSizeMenuOpen(false);
  };

  // Batch 7 · iter_157 — image size / alignment presets. Both act on
  // the currently-selected imageResize node by updating its
  // `containerStyle` (width% + margin) attrs. Reading current width
  // and margin lets us light up the active preset.
  const imgAttrs = editor.isActive('imageResize') ? editor.getAttributes('imageResize') : null;
  const currentImgWidth = (() => {
    const cs = imgAttrs?.containerStyle || '';
    const m = /width:\s*([0-9]+)%/.exec(cs);
    return m ? `${m[1]}%` : null;
  })();
  const currentImgAlignId = (() => {
    const cs = imgAttrs?.containerStyle || '';
    const m = /margin:\s*([^;]+)/.exec(cs);
    if (!m) return null;
    const val = m[1].trim();
    return IMG_ALIGN_PRESETS.find((p) => p.margin === val)?.id || null;
  })();
  // A helper that rebuilds `containerStyle` from parts so width + margin
  // presets don't clobber each other. Both are stored in the SAME
  // containerStyle attribute because the sanitizer allows both `width`
  // and `margin`; keeping them together survives save/reload cleanly.
  const composeContainerStyle = ({ widthPct, marginVal }) => {
    const parts = [];
    parts.push(`width: ${widthPct || '100%'}`);
    parts.push('height: auto');
    parts.push('cursor: pointer');
    if (marginVal) parts.push(`margin: ${marginVal}`);
    return parts.join('; ') + ';';
  };
  const applyImageSize = (preset) => {
    // Preserve current margin (if any) while changing width.
    const cs = imgAttrs?.containerStyle || '';
    const m = /margin:\s*([^;]+)/.exec(cs);
    const marginVal = m ? m[1].trim() : null;
    editor.chain().focus().updateAttributes('imageResize', {
      containerStyle: composeContainerStyle({ widthPct: preset.width, marginVal }),
    }).run();
  };
  const applyImageAlign = (preset) => {
    const cs = imgAttrs?.containerStyle || '';
    const wm = /width:\s*([0-9]+%)/.exec(cs);
    const widthPct = wm ? wm[1] : '100%';
    editor.chain().focus().updateAttributes('imageResize', {
      containerStyle: composeContainerStyle({ widthPct, marginVal: preset.margin }),
    }).run();
  };
  const deleteSelectedImage = () => {
    editor.chain().focus().deleteSelection().run();
  };

  return (
    <div className="relative" data-testid="kb-richeditor-root">
      {/* Always-visible top toolbar (UX refinement). Kept always mounted
          with `display: none` when read-only — conditional-mounting fails
          to commit under React 19 StrictMode + TipTap `useEditor`. */}
      <div className="kb-no-print" style={{ display: editable ? undefined : 'none' }}>
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-lg border border-zinc-200 bg-white px-1.5 py-1 mb-3" data-testid="knowledge-editor-toolbar">
          <ToolbarBtn testid="kb-tb-h1" title="見出し1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><TextHOne size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-h2" title="見出し2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><TextHTwo size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-h3" title="見出し3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><TextHThree size={17} /></ToolbarBtn>
          <span className="mx-1 h-5 w-px bg-zinc-200" />
          <FontSizePicker testidPrefix="kb-tb" currentTier={currentSizeTier} isOpen={sizeMenuOpen} setOpen={setSizeMenuOpen} onPick={applyFontSize} menuRef={sizeMenuRef} />
          <span className="mx-1 h-5 w-px bg-zinc-200" />
          <ToolbarBtn testid="kb-tb-bold" title="太字" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><TextB size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-italic" title="斜体" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><TextItalic size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-underline" title="下線" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><TextUnderline size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-strike" title="打ち消し線" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><TextStrikethrough size={17} /></ToolbarBtn>
          <span className="mx-1 h-5 w-px bg-zinc-200" />
          <ToolbarBtn testid="kb-tb-bullet" title="箇条書き" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><ListBullets size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-ordered" title="番号付きリスト" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListNumbers size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-task" title="ToDoリスト" active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()}><ListChecks size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-quote" title="引用" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quotes size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-code" title="コードブロック" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}><Code size={17} /></ToolbarBtn>
          <span className="mx-1 h-5 w-px bg-zinc-200" />
          <ToolbarBtn testid="kb-tb-link" title="リンク" active={editor.isActive('link')} onClick={openLinkDialog}><LinkSimple size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-image" title="画像" onClick={() => fileRef.current?.click()}><ImageSquare size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-table" title="表を挿入" onClick={() => openTablePicker(null)}><TableIcon size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-hr" title="区切り線" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={17} /></ToolbarBtn>
          <ToolbarBtn testid="kb-tb-pagebreak" title="改ページを挿入" onClick={() => editor.chain().focus().setPageBreak().run()}><ArrowLineDown size={17} /></ToolbarBtn>
          {onCreateComment && (
            <>
              <span className="mx-1 h-5 w-px bg-zinc-200" />
              <ToolbarBtn testid="kb-tb-comment" title="選択部分にコメント" disabled={selEmpty} onClick={addComment}><ChatText size={17} /></ToolbarBtn>
            </>
          )}
        </div>
      </div>

      {/* Bubble menu — appears near the current selection, in addition to
          the always-visible top toolbar. The shouldShow filter hides it
          while an IME composition is in progress. */}
      {editable && (
        <BubbleMenu editor={editor} pluginKey="kbTextBubble" tippyOptions={{ duration: 120, placement: 'top', maxWidth: 'none' }}
          shouldShow={({ editor: ed, from, to }) => {
            if (!ed?.isEditable) return false;
            if (from === to) return false;
            // Don't show inside code blocks or tables — table cells get
            // their own contextual toolbar (Batch 5); code blocks are
            // read-as-code so formatting doesn't apply.
            if (ed.isActive('codeBlock')) return false;
            if (ed.isActive('table')) return false;
            // Batch 7 — a selected image node counts as a NodeSelection
            // (from < to). Suppress the text bubble so the image bubble
            // has the stage.
            if (ed.isActive('imageResize')) return false;
            return true;
          }}>
          <div className="flex items-center gap-0.5 rounded-lg border border-zinc-200 bg-white px-1 py-1 shadow-lg" data-testid="knowledge-editor-bubble">
            <BubbleBtn testid="kb-bb-bold" title="太字" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><TextB size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-bb-italic" title="斜体" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><TextItalic size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-bb-underline" title="下線" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><TextUnderline size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-bb-strike" title="打ち消し線" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><TextStrikethrough size={15} /></BubbleBtn>
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            <FontSizePicker testidPrefix="kb-bb" currentTier={currentSizeTier} isOpen={sizeMenuOpen} setOpen={setSizeMenuOpen} onPick={applyFontSize} menuRef={sizeMenuRef} />
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            <BubbleBtn testid="kb-bb-h1" title="見出し1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><TextHOne size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-bb-h2" title="見出し2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><TextHTwo size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-bb-link" title="リンク" active={editor.isActive('link')} onClick={openLinkDialog}><LinkSimple size={15} /></BubbleBtn>
            {onCreateComment && (
              <>
                <span className="mx-0.5 h-4 w-px bg-zinc-200" />
                <BubbleBtn testid="kb-bb-comment" title="選択部分にコメント" onClick={addComment}><ChatText size={15} /></BubbleBtn>
              </>
            )}
          </div>
        </BubbleMenu>
      )}

      {/* Batch 5 · iter_155 — contextual table toolbar. Shows on any
          cursor position inside a table (regardless of selection).
          Uses a separate pluginKey so it can co-exist with the text
          bubble menu (which is excluded when active('table')). */}
      {editable && (
        <BubbleMenu editor={editor} pluginKey="kbTableBubble"
          tippyOptions={{ duration: 120, placement: 'top', maxWidth: 'none' }}
          shouldShow={({ editor: ed }) => !!ed?.isEditable && ed.isActive('table')}
        >
          <div className="flex items-center gap-0.5 rounded-lg border border-zinc-200 bg-white px-1 py-1 shadow-lg" data-testid="knowledge-editor-table-bubble">
            <BubbleBtn testid="kb-tt-row-above" title="上に行を追加" onClick={() => editor.chain().focus().addRowBefore().run()}><RowsPlusTop size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-tt-row-below" title="下に行を追加" onClick={() => editor.chain().focus().addRowAfter().run()}><RowsPlusBottom size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-tt-row-delete" title="行を削除" onClick={() => editor.chain().focus().deleteRow().run()}><Rows size={15} /></BubbleBtn>
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            <BubbleBtn testid="kb-tt-col-before" title="左に列を追加" onClick={() => editor.chain().focus().addColumnBefore().run()}><ColumnsPlusLeft size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-tt-col-after" title="右に列を追加" onClick={() => editor.chain().focus().addColumnAfter().run()}><ColumnsPlusRight size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-tt-col-delete" title="列を削除" onClick={() => editor.chain().focus().deleteColumn().run()}><Columns size={15} /></BubbleBtn>
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            <BubbleBtn testid="kb-tt-header-row" title="ヘッダー行を切替" active={editor.isActive('tableHeader') && editor.state.selection.$anchor.parent.type.name === 'tableHeader'}
              onClick={() => editor.chain().focus().toggleHeaderRow().run()}>
              <span className="text-[10px] font-bold">H↔</span>
            </BubbleBtn>
            <BubbleBtn testid="kb-tt-header-col" title="ヘッダー列を切替" onClick={() => editor.chain().focus().toggleHeaderColumn().run()}>
              <span className="text-[10px] font-bold">H↕</span>
            </BubbleBtn>
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            {/* Alignment — reads current cell's `align` for active state */}
            <BubbleBtn testid="kb-tt-align-left" title="左揃え" active={editor.getAttributes('tableCell').align === 'left' || editor.getAttributes('tableHeader').align === 'left'}
              onClick={() => editor.chain().focus().setCellAttribute('align', 'left').run()}><TextAlignLeft size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-tt-align-center" title="中央揃え" active={editor.getAttributes('tableCell').align === 'center' || editor.getAttributes('tableHeader').align === 'center'}
              onClick={() => editor.chain().focus().setCellAttribute('align', 'center').run()}><TextAlignCenter size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-tt-align-right" title="右揃え" active={editor.getAttributes('tableCell').align === 'right' || editor.getAttributes('tableHeader').align === 'right'}
              onClick={() => editor.chain().focus().setCellAttribute('align', 'right').run()}><TextAlignRight size={15} /></BubbleBtn>
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            {/* Background palette — 8 preset swatches + clear */}
            <div className="inline-flex items-center gap-0.5 px-0.5">
              <PaintBucket size={13} weight="duotone" className="text-zinc-400" />
              {CELL_BG_SWATCHES.map((s) => (
                <button key={s.id} type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => editor.chain().focus().setCellAttribute('backgroundColor', s.color).run()}
                  data-testid={`kb-tt-bg-${s.id}`}
                  title={s.label}
                  className={`h-4 w-4 rounded-sm border ${s.color ? 'border-zinc-300 hover:ring-2 hover:ring-blue-400' : 'border-zinc-300 bg-white hover:ring-2 hover:ring-blue-400'} transition-shadow`}
                  style={s.color ? { backgroundColor: s.color } : { background: 'repeating-linear-gradient(45deg, #fff, #fff 3px, #f3f4f6 3px, #f3f4f6 5px)' }}
                />
              ))}
            </div>
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            {/* Merge / split — TipTap 2 supports these without schema
                rewrite (verified in Batch 5 audit). */}
            <BubbleBtn testid="kb-tt-merge" title="セル結合" onClick={() => editor.chain().focus().mergeCells().run()}><ArrowsInLineHorizontal size={15} /></BubbleBtn>
            <BubbleBtn testid="kb-tt-split" title="セル分割" onClick={() => editor.chain().focus().splitCell().run()}><ArrowsOutLineHorizontal size={15} /></BubbleBtn>
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            <BubbleBtn testid="kb-tt-delete" title="表を削除" onClick={() => editor.chain().focus().deleteTable().run()}><XCircle size={15} weight="fill" /></BubbleBtn>
          </div>
        </BubbleMenu>
      )}

      {/* Batch 7 · iter_157 — contextual image toolbar. Appears when an
          `imageResize` node is selected. Offers size presets and
          alignment; delete removes just the image node. Separate
          pluginKey so it coexists with the text and table bubbles. */}
      {editable && (
        <BubbleMenu editor={editor} pluginKey="kbImageBubble"
          tippyOptions={{ duration: 120, placement: 'top', maxWidth: 'none' }}
          shouldShow={({ editor: ed }) => !!ed?.isEditable && ed.isActive('imageResize')}
        >
          <div className="flex items-center gap-0.5 rounded-lg border border-zinc-200 bg-white px-1 py-1 shadow-lg" data-testid="knowledge-editor-image-bubble">
            {IMG_SIZE_PRESETS.map((p) => (
              <BubbleBtn key={p.id} testid={`kb-img-size-${p.id}`} title={p.label}
                active={currentImgWidth === p.width}
                onClick={() => applyImageSize(p)}
              >
                <span className="text-[10px] font-semibold">{p.label}</span>
              </BubbleBtn>
            ))}
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            {IMG_ALIGN_PRESETS.map((p) => (
              <BubbleBtn key={p.id} testid={`kb-img-align-${p.id}`} title={p.label}
                active={currentImgAlignId === p.id}
                onClick={() => applyImageAlign(p)}
              >
                {p.id === 'left' && <TextAlignLeft size={15} />}
                {p.id === 'center' && <TextAlignCenter size={15} />}
                {p.id === 'right' && <TextAlignRight size={15} />}
              </BubbleBtn>
            ))}
            <span className="mx-0.5 h-4 w-px bg-zinc-200" />
            <BubbleBtn testid="kb-img-delete" title="画像を削除" onClick={deleteSelectedImage}>
              <Trash size={15} />
            </BubbleBtn>
          </div>
        </BubbleMenu>
      )}

      {/* Batch 5 — row/col size picker popover. Rendered as position:fixed
          so it sits above the editor and can be clamped inside the
          viewport. Triggered from the slash-menu 表 item OR the pinned
          toolbar's table button. */}
      {editable && tablePicker && (
        <div style={{ position: 'fixed', top: tablePicker.top, left: tablePicker.left, zIndex: 60 }}
          data-testid="kb-table-picker">
          <TableSizePicker
            onPick={(r, c) => commitTable(r, c)}
            onClose={() => setTablePicker(null)}
          />
        </div>
      )}

      <EditorContent editor={editor} />
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onImagePicked} data-testid="knowledge-image-input" />

      {/* Batch 6 · iter_156 — Link picker dialog. Renders lazily. */}
      {linkDialog?.open && (
        <LinkPickerDialog
          open
          initial={linkDialog.initial}
          tree={tree}
          onClose={() => setLinkDialog(null)}
          onSave={(payload) => { applyLinkFromDialog(payload); setLinkDialog(null); }}
          onRemove={() => { applyLinkRemove(); setLinkDialog(null); }}
        />
      )}

      {slash && (
        <div className="fixed z-50 w-60 max-h-72 overflow-y-auto rounded-lg border border-zinc-200 bg-white py-1 shadow-xl"
          style={{ top: slash.top, left: slash.left }} data-testid="knowledge-slash-menu">
          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">ブロック</div>
          {slash.items.map((it, i) => (
            <button key={it.id} type="button" onMouseDown={(e) => { e.preventDefault(); runItem(it); }}
              data-testid={`knowledge-slash-${it.id}`}
              className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm ${i === slash.index ? 'bg-blue-50 text-blue-700' : 'text-zinc-700 hover:bg-zinc-50'}`}>
              <it.Icon size={16} className="shrink-0 text-zinc-400" />{it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
